/**
 * React hooks for ERP API client
 * Provides React-friendly wrappers around the ERP service client
 * Includes circuit breaker and request throttling to prevent API bombardment
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { erpClient, getERPBaseUrl } from '@/services/clients';
import { store } from '@/store';
import { settingsCache } from '@/services/settingsCache';
import type { ApiResponse, SaleCancellationResponse, UpdateSaleRequest } from '@kisanlink/erp-service';

interface UseQueryOptions<T> {
  enabled?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  refetchInterval?: number; // Optional auto-refetch interval in ms
  staleTime?: number; // Time before data is considered stale (default: 0)
  cacheTime?: number; // Time to keep data in cache (default: 5 minutes)
  queryKey?: string; // Unique key for caching this query
}

// Circuit breaker state
interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number;
  state: 'closed' | 'open' | 'half-open';
}

const circuitBreakers = new Map<string, CircuitBreakerState>();
const FAILURE_THRESHOLD = 5;
const TIMEOUT_DURATION = 30000; // 30 seconds
const HALF_OPEN_TIMEOUT = 10000; // 10 seconds

// Request cache
const requestCache = new Map<string, { data: unknown; timestamp: number }>();
const DEFAULT_CACHE_TIME = 300000; // 5 minutes

// Export cache clearing utility
export function invalidateCache(pattern?: string) {
  if (!pattern) {
    requestCache.clear();
    return;
  }
  for (const key of requestCache.keys()) {
    if (key.includes(pattern)) {
      requestCache.delete(key);
    }
  }
}

interface UseQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  forceRefetch: () => Promise<void>;
}

interface UseMutationResult<TData, TVariables> {
  mutate: (variables: TVariables) => Promise<TData>;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  isLoading: boolean;
  error: Error | null;
  data: TData | undefined;
}

/**
 * Check circuit breaker state
 */
function checkCircuitBreaker(key: string): boolean {
  const breaker = circuitBreakers.get(key);

  if (!breaker || breaker.state === 'closed') {
    return true;
  }

  if (breaker.state === 'open') {
    const now = Date.now();
    if (now - breaker.lastFailureTime > HALF_OPEN_TIMEOUT) {
      circuitBreakers.set(key, { ...breaker, state: 'half-open' });
      return true;
    }
    return false;
  }

  return true; // half-open state
}

/**
 * Record circuit breaker failure
 */
function recordFailure(key: string): void {
  const breaker = circuitBreakers.get(key) || {
    failures: 0,
    lastFailureTime: 0,
    state: 'closed' as const,
  };

  breaker.failures += 1;
  breaker.lastFailureTime = Date.now();

  if (breaker.failures >= FAILURE_THRESHOLD) {
    breaker.state = 'open';
  }

  circuitBreakers.set(key, breaker);
}

/**
 * Record circuit breaker success
 */
function recordSuccess(key: string): void {
  circuitBreakers.set(key, {
    failures: 0,
    lastFailureTime: 0,
    state: 'closed',
  });
}

/**
 * Generic hook for fetching data from the ERP API with circuit breaker
 */
export function useQuery<T>(
  queryFn: () => Promise<ApiResponse<T>>,
  options: UseQueryOptions<T> = {}
): UseQueryResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const {
    enabled = true,
    onSuccess,
    onError,
    staleTime = 0,
    cacheTime = DEFAULT_CACHE_TIME,
    refetchInterval,
    queryKey: providedQueryKey,
  } = options;

  // Use provided queryKey or fallback to function string
  // Note: All hooks must provide queryKey to avoid cache collisions
  const queryKey = providedQueryKey || `query-${Math.random()}`;
  const isMountedRef = useRef(true);
  const lastFetchTime = useRef<number>(0);
  
  // Store queryFn in a ref so it doesn't cause fetchData to be recreated
  // This prevents unnecessary API calls when the component re-renders
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!enabled) {
        setIsLoading(false);
        return;
      }

      // Check circuit breaker
      if (!checkCircuitBreaker(queryKey)) {
        const error = new Error('Circuit breaker is open. Too many failed requests.');
        setError(error);
        setIsLoading(false);
        if (onError) {
          onError(error);
        }
        return;
      }

      const now = Date.now();

      // Check cache (skip if force refresh)
      if (!forceRefresh) {
        const cached = requestCache.get(queryKey);
        if (cached && now - cached.timestamp < staleTime) {
          setData(cached.data as T);
          setIsLoading(false);
          return;
        }
      } else {
        // Clear cache entry when force refreshing
        requestCache.delete(queryKey);
      }

      // Throttle requests - prevent rapid successive calls (skip if force refresh)
      if (!forceRefresh && now - lastFetchTime.current < 1000) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        lastFetchTime.current = now;

        // Use the ref to get the latest queryFn without including it in dependencies
        const response = await Promise.race([
          queryFnRef.current(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), TIMEOUT_DURATION)
          ),
        ]);

        if (!isMountedRef.current) return;

        const responseData = response.data as T;
        setData(responseData);

        // Cache the response
        requestCache.set(queryKey, { data: responseData, timestamp: now });

        // Clean old cache entries
        setTimeout(() => {
          requestCache.delete(queryKey);
        }, cacheTime);

        recordSuccess(queryKey);

        if (onSuccess && responseData !== undefined) {
          onSuccess(responseData);
        }
      } catch (err) {
        if (!isMountedRef.current) return;

        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        recordFailure(queryKey);

        if (onError) {
          onError(error);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [enabled, onSuccess, onError, queryKey, staleTime, cacheTime] // Removed queryFn from deps
  );

  useEffect(() => {
    isMountedRef.current = true;
    
    // Only fetch if enabled
    if (enabled) {
      console.log(`[useQuery] 🔥 Fetching enabled for ${queryKey}`);
      fetchData();
    } else {
      console.log(`[useQuery] ⏸️  Fetching disabled for ${queryKey}`);
      setIsLoading(false);
      setData(undefined);
    }

    // Set up auto-refetch if interval is provided
    let intervalId: NodeJS.Timeout | undefined;
    if (refetchInterval && enabled) {
      intervalId = setInterval(fetchData, refetchInterval);
    }

    return () => {
      isMountedRef.current = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchData, refetchInterval, enabled, queryKey]);

  // Create a force refetch function that bypasses cache
  const forceRefetch = useCallback(() => fetchData(true), [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    forceRefetch,
  };
}

/**
 * Generic hook for mutations (create, update, delete operations)
 */
export function useMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  options?: { onSuccess?: (data: TData) => void }
): UseMutationResult<TData, TVariables> {
  const [data, setData] = useState<TData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (variables: TVariables): Promise<TData> => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await mutationFn(variables);
        const responseData = response.data as TData;
        setData(responseData);
        
        if (options?.onSuccess) {
          options.onSuccess(responseData);
        }
        
        return responseData;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [mutationFn, options]
  );

  return {
    mutate,
    mutateAsync: mutate,
    isLoading,
    error,
    data,
  };
}

// Specific hooks for common API operations with sensible caching

/**
 * Sales list hook - cache for 2 minutes, data changes frequently
 * Note: List response does NOT include items and breakdown for performance
 */
export const useSalesList = (params?: Parameters<typeof erpClient.sales.list>[0]) => {
  const queryKey = `sales-list-${JSON.stringify(params || {})}`;
  return useQuery(() => erpClient.sales.list(params || {}), {
    queryKey,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Get single sale by ID - includes full details with items and breakdown
 */
export const useSaleDetail = (id: string | null | undefined) => {
  const queryKey = `sale-detail-${id}`;
  return useQuery(() => erpClient.sales.get(id!), {
    queryKey,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
    enabled: !!id && id.trim().length > 0, // Only fetch if ID is provided
  });
};

/**
 * Collaborators list hook - cache for 5 minutes, relatively stable data
 */
export const useCollaboratorsList = (
  params?: Parameters<typeof erpClient.collaborators.list>[0]
) => {
  const queryKey = `collaborators-list-${JSON.stringify(params || {})}`;
  return useQuery(() => erpClient.collaborators.list(params), {
    queryKey,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

/**
 * Collaborator variants hook - cache for 2 minutes, data changes moderately
 * Fetches variants for a specific collaborator using GET /api/v1/collaborators/{id}/variants
 */
export const useCollaboratorVariants = (
  collaboratorId: string,
  params?: { limit?: number; offset?: number }
) => {
  // Use useMemo to ensure query function is recreated when collaboratorId or params change
  const queryFn = useMemo(
    () => async () => {
      // Prefer SDK method if available
      const collabService: any = (erpClient as any)?.collaborators;
      if (collabService && typeof collabService.getVariants === 'function') {
        return collabService.getVariants(collaboratorId, params);
      }

      // Fallback: direct fetch if SDK is missing getVariants
      const baseUrl = getERPBaseUrl();
      const token = store.getState().auth?.accessToken;
      const url = new URL(`${baseUrl.replace(/\/$/, '')}/api/v1/collaborators/${collaboratorId}/variants`);
      if (params?.limit !== undefined) url.searchParams.set('limit', String(params.limit));
      if (params?.offset !== undefined) url.searchParams.set('offset', String(params.offset));

      const res = await fetch(url.toString(), {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`GET ${url.pathname} failed: ${res.status} ${text || res.statusText}`);
      }
      return res.json();
    },
    [collaboratorId, params]
  );
  
  const queryKey = `collaborator-variants-${collaboratorId}-${JSON.stringify(params || {})}`;
  return useQuery(queryFn, {
    enabled: !!collaboratorId && collaboratorId.trim().length > 0,
    queryKey,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Collaborator statistics hook - cache for 1 minute, data changes frequently
 */
export const useCollaboratorStats = (collaboratorId: string) => {
  // Build query function with fallback if SDK method is missing
  const queryFn = useMemo(
    () => async () => {
      const collabService: any = (erpClient as any)?.collaborators;
      if (collabService && typeof collabService.getStats === 'function') {
        return collabService.getStats(collaboratorId);
      }

      // Fallback: direct fetch
      const baseUrl = getERPBaseUrl();
      const token = store.getState().auth?.accessToken;
      const url = `${baseUrl.replace(/\/$/, '')}/api/v1/collaborators/${collaboratorId}/stats`;

      const res = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`GET ${url} failed: ${res.status} ${text || res.statusText}`);
      }
      return res.json();
    },
    [collaboratorId]
  );

  const queryKey = `collaborator-stats-${collaboratorId}`;
  return useQuery(queryFn, {
    enabled: !!collaboratorId && collaboratorId.trim().length > 0,
    queryKey,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Products list hook - cache for 5 minutes, relatively stable data
 */
export const useProductsList = (params?: Parameters<typeof erpClient.products.list>[0]) => {
  const queryKey = `products-list-${JSON.stringify(params || {})}`;
  return useQuery(() => erpClient.products.list(params), {
    queryKey,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

/**
 * Product variants list hook - cache for 5 minutes
 * @param productId - Product ID to fetch variants for
 * @param enabled - Whether the query should run (default: true)
 */
export const useProductVariantsList = (productId: string, enabled: boolean = true) => {
  return useQuery(() => erpClient.products.variants.list(productId), {
    enabled: enabled && !!productId,
    queryKey: `product-variants-${productId}`,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};


/**
 * Inventory batches list hook - cache for 1 minute, changes frequently
 */
export const useInventoryBatchesList = (
  params?: Parameters<typeof erpClient.inventory.batches.list>[0]
) => {
  const queryKey = `inventory-batches-${JSON.stringify(params || {})}`;
  return useQuery(() => erpClient.inventory.batches.list(params), {
    queryKey,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Low stock batches hook - cache for 2 minutes
 * @param threshold - Stock threshold (default: 10)
 */
export const useLowStockBatches = (threshold: number = 10) => {
  return useQuery(() => erpClient.inventory.batches.getLowStock(threshold), {
    queryKey: `low-stock-batches-${threshold}`,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Expiring batches hook - cache for 5 minutes
 * @param days - Days until expiry (default: 30)
 */
export const useExpiringBatches = (days: number = 30) => {
  return useQuery(() => erpClient.inventory.batches.getExpiring(days), {
    queryKey: `expiring-batches-${days}`,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

/**
 * Batch transactions hook - cache for 1 minute
 * @param batchId - Batch ID to get transactions for
 * @param params - Optional filter parameters
 */
export const useBatchTransactions = (
  batchId: string,
  params?: Parameters<typeof erpClient.inventory.batches.getTransactions>[1]
) => {
  const queryKey = `batch-transactions-${batchId}-${JSON.stringify(params || {})}`;
  return useQuery(() => erpClient.inventory.batches.getTransactions(batchId, params), {
    queryKey,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Product availability hook - cache for 2 minutes, changes moderately
 * Returns availability of all products across all warehouses
 */
export const useProductAvailability = () => {
  return useQuery(() => erpClient.inventory.stock.checkAvailability(), {
    queryKey: 'product-availability',
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Warehouses list hook - cache for 10 minutes, very stable data
 */
export const useWarehousesList = (params?: Parameters<typeof erpClient.warehouses.list>[0]) => {
  const queryKey = `warehouses-list-${JSON.stringify(params || {})}`;
  return useQuery(() => erpClient.warehouses.list(params), {
    queryKey,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

/**
 * Warehouse detail hook - cache for 10 minutes
 */
export const useWarehouseDetail = (id: string) => {
  return useQuery(() => erpClient.warehouses.get(id), {
    enabled: !!id,
    queryKey: `warehouse-detail-${id}`,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

// Mutation hooks for create, update, delete operations

/**
 * Product mutations
 */
export const useCreateProductMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation((data: unknown) => erpClient.products.create(data as any));
};

export const useUpdateProductMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.products.update(id, data as any)
  );
};

export const useDeleteProductMutation = () => {
  return useMutation((id: string) => erpClient.products.delete(id));
};

/**
 * Product variant mutations
 * Updated to use POST /api/v1/products/:productId/variants with collaborator_ids array
 */
export const useCreateProductVariantMutation = () => {
  return useMutation(({ productId, data }: { productId: string; data: unknown }) => {
    // Type assertion needed because TypeScript types may not be updated yet
    // The service method signature is: create(productId: string, payload: CreateProductVariantRequest)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (erpClient.products.variants.create as any)(productId, data);
  });
};

export const useUpdateProductVariantMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.products.variants.update(id, data as any)
  );
};

export const useDeleteProductVariantMutation = () => {
  return useMutation((id: string) => erpClient.products.variants.delete(id));
};

/**
 * Get variant by SKU
 */
export const useVariantBySku = (sku: string | null) => {
  return useQuery(
    () => {
      if (!sku) throw new Error('SKU is required');
      
      // Check if getBySku method exists, otherwise use direct API call
      const variantsService = erpClient.products.variants as any;
      if (variantsService.getBySku && typeof variantsService.getBySku === 'function') {
        return variantsService.getBySku(sku);
      } else {
        // Fallback: Use fetch directly
        const baseURL = getERPBaseUrl();
        const token = store.getState().auth.accessToken;
        return fetch(`${baseURL.replace(/\/$/, '')}/api/v1/variants/sku/${sku}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        }).then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return res.json();
        });
      }
    },
    {
      enabled: !!sku && sku.trim().length > 0,
      staleTime: 60000, // 1 minute
      cacheTime: 300000, // 5 minutes
    }
  );
};

/**
 * Product price mutations
 */
export const useCreateProductPriceMutation = () => {
  return useMutation(async (data: { variant_id: string; [key: string]: unknown }) => {
    const { variant_id, ...priceData } = data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = await erpClient.products.variants.prices.create(variant_id, priceData as any);
    return result;
  });
};

export const useUpdateProductPriceMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.products.prices.update(id, data as any)
  );
};

export const useDeleteProductPriceMutation = () => {
  return useMutation((id: string) => erpClient.products.prices.delete(id));
};

export const useBulkUpdatePricesMutation = () => {
  return useMutation(
    (
      updates: Array<{
        product_id: string;
        variant_id?: string;
        price_type: string;
        price: number;
      }>
    ) => erpClient.products.prices.bulkUpdate(updates)
  );
};

/**
 * Sales mutations
 */
export const useCreateSaleMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation((data: unknown) => erpClient.sales.create(data as any));
};

export const usePatchSaleMutation = () => {
  return useMutation(({ id, data }: { id: string; data: Partial<UpdateSaleRequest> }) =>
    (erpClient.sales as any).patch(id, data)
  );
};

export const useUpdateSaleMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.sales.update(id, data as any)
  );
};

export const useUpdateSaleStatusMutation = () => {
  return useMutation(({ id, status }: { id: string; status: string }) =>
    erpClient.sales.updateStatus(id, { status })
  );
};

export const useDeleteSaleMutation = () => {
  return useMutation((id: string) => erpClient.sales.delete(id));
};

/**
 * Complete sale mutation (updates status to 'completed')
 * Note: Cancel hooks already exist below (useCancelSaleMutation, useCancelSaleItemsMutation)
 */
export const useCompleteSaleMutation = () => {
  return useMutation((id: string) =>
    erpClient.sales.updateStatus(id, { status: 'completed' })
  );
};

/**
 * Collaborator mutations
 */
export const useCreateCollaboratorMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation((data: unknown) => erpClient.collaborators.create(data as any));
};

export const useUpdateCollaboratorMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.collaborators.update(id, data as any)
  );
};

export const useDeleteCollaboratorMutation = () => {
  return useMutation((id: string) => erpClient.collaborators.delete(id));
};

/**
 * Inventory mutations
 */
export const useCreateInventoryBatchMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation((data: unknown) => erpClient.inventory.batches.create(data as any));
};

export const useUpdateInventoryBatchMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.inventory.batches.update(id, data as any)
  );
};

export const useDeleteInventoryBatchMutation = () => {
  return useMutation((id: string) => erpClient.inventory.batches.delete(id));
};

/**
 * Batch transaction mutations
 */
export const useCreateBatchTransactionMutation = () => {
  return useMutation(({ batchId, data }: { batchId: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.inventory.transactions.create(batchId, data as any)
  );
};

/**
 * Purchase Order hooks
 */
export const usePurchaseOrdersList = (
  params?: Parameters<typeof erpClient.purchaseOrders.list>[0]
) => {
  return useQuery(() => erpClient.purchaseOrders.list(params), {
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

export const usePurchaseOrderDetail = (id: string) => {
  return useQuery(() => erpClient.purchaseOrders.get(id), {
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
    enabled: !!id && id.trim().length > 0, // Only fetch if ID is provided
  });
};

export const useCreatePurchaseOrderMutation = () => {
  return useMutation((data: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.purchaseOrders.create(data as any)
  );
};

export const useUpdatePurchaseOrderMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.purchaseOrders.update(id, data as any)
  );
};

export const useUpdatePurchaseOrderStatusMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.purchaseOrders.updateStatus(id, data as any)
  );
};

export const useUpdatePurchaseOrderPaymentMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.purchaseOrders.updatePayment(id, data as any)
  );
};

export const useDeletePurchaseOrderMutation = () => {
  return useMutation((id: string) => erpClient.purchaseOrders.delete(id));
};

/**
 * GRN hooks
 */
export const useGRNsList = (params?: Parameters<typeof erpClient.grns.list>[0]) => {
  return useQuery(() => erpClient.grns.list(params), {
    staleTime: 60000, // 1 minute - data changes moderately
    cacheTime: 300000, // 5 minutes
  });
};

export const useGRNDetail = (id: string) => {
  return useQuery(() => erpClient.grns.get(id), {
    enabled: !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * GRN mutations
 */
export const useCreateGRNMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation((data: unknown) => erpClient.grns.create(data as any));
};

export const useUpdateGRNMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.grns.update(id, data as any)
  );
};

export const useDeleteGRNMutation = () => {
  return useMutation((id: string) => erpClient.grns.delete(id));
};

/**
 * Warehouse mutations
 */
export const useCreateWarehouseMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation((data: unknown) => erpClient.warehouses.create(data as any));
};

export const useUpdateWarehouseMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.warehouses.update(id, data as any)
  );
};

export const useDeleteWarehouseMutation = () => {
  return useMutation((id: string) => erpClient.warehouses.delete(id));
};

/**
 * Batch detail hook - cache for 1 minute
 */
export const useBatchDetail = (id: string) => {
  return useQuery(() => erpClient.inventory.batches.get(id), {
    enabled: !!id && id.trim().length > 0, // Only fetch if ID is provided
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Product Variants hooks - cache for 5 minutes, relatively stable data
 * Note: Current API requires productId, spec suggests global list - using empty string as placeholder
 */
export const useVariantsList = (productId: string = '') => {
  return useQuery(() => erpClient.products.variants.list(productId), {
    enabled: !!productId,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

export const useVariantDetail = (
  productId: string,
  variantId: string,
  options?: UseQueryOptions<unknown>
) => {
  return useQuery(() => erpClient.products.variants.get(productId, variantId), {
    enabled: !!productId && !!variantId,
    ...options,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

/**
 * Product Prices hooks - cache for 5 minutes, relatively stable data
 * Note: Current API requires productId, spec suggests global list - using empty string as placeholder
 */
export const usePricesList = (variantId: string = '') => {
  return useQuery(
    async () => {
      if (!variantId) {
        return {
          success: true,
          data: [],
          message: 'No variant ID',
          timestamp: new Date().toISOString(),
        };
      }
      return erpClient.products.variants.prices.list(variantId);
    },
    {
      enabled: !!variantId,
      staleTime: 300000, // 5 minutes
      cacheTime: 600000, // 10 minutes
    }
  );
};

// Hook to fetch prices for multiple variants at once
export const useMultipleVariantPrices = (variantIds: string[] = []) => {
  // Create a unique query key based on the sorted variant IDs
  // Include timestamp to avoid caching issues across different products
  const sortedIds = variantIds.sort().join(',');
  const queryKey = sortedIds ? `variant-prices-${sortedIds}` : `variant-prices-empty-${Date.now()}`;

  return useQuery(
    async () => {
      if (!variantIds || variantIds.length === 0) {
        return { data: {} } as ApiResponse<Record<string, unknown[]>>;
      }

      // Fetch prices for all variants in parallel using SDK
      const pricePromises = variantIds.map(async (variantId) => {
        try {
          const result = await erpClient.products.variants.prices.list(variantId);
          // Handle null or undefined data from backend - return empty array instead
          const pricesArray = result.data != null ? result.data : [];
          return { variantId, prices: Array.isArray(pricesArray) ? pricesArray : [] };
        } catch (error) {
          return { variantId, prices: [] };
        }
      });

      const results = await Promise.all(pricePromises);

      // Convert array to map for easy lookup
      const pricesMap: Record<string, unknown[]> = {};
      results.forEach(({ variantId, prices }) => {
        pricesMap[variantId] = prices;
      });

      return { data: pricesMap } as ApiResponse<Record<string, unknown[]>>;
    },
    {
      enabled: variantIds.length > 0,
      queryKey,
      staleTime: 300000, // 5 minutes
      cacheTime: 600000, // 10 minutes
    }
  );
};

/**
 * Get active price for a product variant
 * @param productId - Product ID
 * @param priceType - Price type (PURCHASE, SALE, MRP)
 * @param options - Query options
 */
export const useActivePriceQuery = (
  productId: string,
  priceType: 'PURCHASE' | 'SALE' | 'MRP' = 'SALE',
  options?: UseQueryOptions<unknown>
) => {
  return useQuery(() => erpClient.products.prices.getActive(productId, priceType), {
    enabled: !!productId && options?.enabled !== false,
    ...options,
    staleTime: 300000, // 5 minutes - prices don't change frequently
    cacheTime: 600000, // 10 minutes
  });
};

/**
 * Discount hooks - cache for 2 minutes, data changes moderately
 */
export const useDiscountsList = (params?: Parameters<typeof erpClient.discounts.list>[0]) => {
  return useQuery(() => erpClient.discounts.list(params), {
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

export const useDiscountDetail = (id: string) => {
  return useQuery(() => erpClient.discounts.get(id), {
    enabled: !!id,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

export const useDiscountByCodeQuery = (code: string, options?: UseQueryOptions<unknown>) => {
  return useQuery(() => erpClient.discounts.getByCode(code), {
    enabled: !!code && options?.enabled !== false,
    ...options,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Discount mutations
 */
export const useCreateDiscountMutation = () => {
  return useMutation((data: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.discounts.create(data as any)
  );
};

export const useUpdateDiscountMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.discounts.update(id, data as any)
  );
};

export const useDeleteDiscountMutation = () => {
  return useMutation((id: string) => erpClient.discounts.delete(id));
};

export const useToggleDiscountMutation = () => {
  return useMutation(({ id, isActive }: { id: string; isActive: boolean }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.discounts.update(id, { is_active: isActive } as any)
  );
};

export const useCalculateDiscountMutation = () => {
  return useMutation((data: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.discounts.validate(data as any)
  );
};

/**
 * Category hooks - cache for 10 minutes, very stable data
 * Note: Using type assertions as erpClient types may not be updated yet
 */
export const useCategoriesList = () => {
  // Memoize the query function to prevent recreation on every render
  // This prevents unnecessary API calls when typing in form fields
  const queryFn = useMemo(
    () => () => (erpClient as any).categories.list(),
    [] // Empty deps - this function never changes
  );
  
  return useQuery(queryFn, {
    queryKey: 'categories-list', // Stable query key for caching
    staleTime: 600000, // 10 minutes - categories are very stable
    cacheTime: 1800000, // 30 minutes
  });
};

export const useCategoryDetail = (id: string) => {
  return useQuery(() => (erpClient as any).categories.get(id), {
    enabled: !!id,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

export const useCategoryByName = (name: string) => {
  return useQuery(() => (erpClient as any).categories.getByName(name), {
    enabled: !!name,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

/**
 * Category mutations
 */
export const useCreateCategoryMutation = () => {
  return useMutation((data: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (erpClient as any).categories.create(data as any)
  );
};

export const useUpdateCategoryMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (erpClient as any).categories.update(id, data as any)
  );
};

export const useDeleteCategoryMutation = () => {
  return useMutation((id: string) => (erpClient as any).categories.delete(id));
};

export const useSeedCategoriesMutation = () => {
  return useMutation(() => (erpClient as any).categories.seed());
};

/**
 * Subcategory hooks - cache for 10 minutes, very stable data
 * Note: Using type assertions as erpClient types may not be updated yet
 */
export const useSubcategoriesList = () => {
  return useQuery(() => (erpClient as any).subcategories.list(), {
    staleTime: 600000, // 10 minutes - subcategories are very stable
    cacheTime: 1800000, // 30 minutes
  });
};

export const useSubcategoryDetail = (id: string) => {
  return useQuery(() => (erpClient as any).subcategories.get(id), {
    enabled: !!id,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

export const useSubcategoryByName = (name: string) => {
  return useQuery(() => (erpClient as any).subcategories.getByName(name), {
    enabled: !!name,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

export const useSubcategoriesByCategory = (categoryId: string, options?: { enabled?: boolean }) => {
  // Use useMemo to create a stable query function that only changes when categoryId changes
  // This prevents the query from being recreated on every render
  const queryFn = useMemo(
    () => () => (erpClient as any).subcategories.getByCategory(categoryId),
    [categoryId]
  );
  
  const enabled = options?.enabled !== undefined ? options.enabled : !!categoryId;
  
  return useQuery(queryFn, {
    enabled,
    queryKey: `subcategories-category-${categoryId}`, // Stable query key based on categoryId
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

/**
 * Subcategory mutations
 */
export const useCreateSubcategoryMutation = () => {
  return useMutation((data: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (erpClient as any).subcategories.create(data as any)
  );
};

export const useUpdateSubcategoryMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (erpClient as any).subcategories.update(id, data as any)
  );
};

export const useDeleteSubcategoryMutation = () => {
  return useMutation((id: string) => (erpClient as any).subcategories.delete(id));
};

/**
 * Returns hooks - cache for 2 minutes, data changes moderately
 */
export const useReturnsList = (params?: Parameters<typeof erpClient.returns.list>[0]) => {
  return useQuery(() => erpClient.returns.list(params), {
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

export const useReturnDetail = (id: string) => {
  return useQuery(() => erpClient.returns.get(id), {
    enabled: !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

export const useReturnsBySaleQuery = (saleId: string, options?: UseQueryOptions<unknown>) => {
  return useQuery(() => erpClient.returns.list({ sale_id: saleId }), {
    enabled: !!saleId && options?.enabled !== false,
    ...options,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

export const useReturnMetricsQuery = (
  params?: Parameters<typeof erpClient.returns.getMetrics>[0],
  options?: UseQueryOptions<unknown>
) => {
  return useQuery(() => erpClient.returns.getMetrics(params), {
    enabled: options?.enabled !== false,
    ...options,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

export const useMostReturnedProductsQuery = (
  params?: Parameters<typeof erpClient.returns.getMostReturned>[0],
  options?: UseQueryOptions<unknown>
) => {
  return useQuery(() => erpClient.returns.getMostReturned(params), {
    enabled: options?.enabled !== false,
    ...options,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });
};

/**
 * Returns mutations
 */
export const useCreateReturnMutation = () => {
  return useMutation((data: unknown) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.returns.create(data as any)
  );
};

export const useUpdateReturnMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.returns.update(id, data as any)
  );
};

export const useUpdateReturnStatusMutation = () => {
  return useMutation(({ id, data }: { id: string; data: unknown }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    erpClient.returns.updateStatus(id, data as any)
  );
};

export const useApproveReturnMutation = () => {
  return useMutation(({ id, notes }: { id: string; notes?: string }) =>
    erpClient.returns.approve(id, notes)
  );
};

export const useRejectReturnMutation = () => {
  return useMutation(({ id, reason }: { id: string; reason: string }) =>
    erpClient.returns.reject(id, reason)
  );
};

export const useProcessReturnMutation = () => {
  return useMutation((id: string) => erpClient.returns.process(id));
};

export const useProcessRefundMutation = () => {
  return useMutation(
    ({
      id,
      data,
    }: {
      id: string;
      data: { refund_amount: number; refund_method: string; notes?: string };
    }) => erpClient.returns.refund(id, data)
  );
};

export const useDeleteReturnMutation = () => {
  return useMutation((id: string) => erpClient.returns.delete(id));
};

/**
 * Refund Policies hooks
 */
export const useRefundPoliciesList = (
  params?: Parameters<typeof erpClient.returns.refundPolicies.list>[0]
) => {
  return useQuery(() => erpClient.returns.refundPolicies.list(params), {
    staleTime: 600000, // 10 minutes - very stable data
    cacheTime: 1800000, // 30 minutes
  });
};

export const useRefundPolicyDetail = (id: string) => {
  return useQuery(() => erpClient.returns.refundPolicies.get(id), {
    enabled: !!id,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

/**
 * Attachment hooks - cache for 2 minutes, data changes moderately
 */
export const useAttachmentsList = (entityType: string, entityId: string) => {
  return useQuery(() => erpClient.attachments.getByEntity(entityType, entityId), {
    enabled: !!entityType && !!entityId,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

export const useAttachmentDownloadUrl = (id: string, options?: UseQueryOptions<unknown>) => {
  return useQuery(() => erpClient.attachments.getDownloadUrl(id), {
    enabled: !!id && options?.enabled !== false,
    ...options,
    staleTime: 0, // Don't cache download URLs (they expire)
    cacheTime: 0,
  });
};

/**
 * Attachment mutations
 */
export const useUploadAttachmentMutation = () => {
  return useMutation(
    async ({
      entityType,
      entityId,
      file,
    }: {
      entityType: string;
      entityId: string;
      file: File;
    }) => {
      const result = await erpClient.attachments.upload(file, entityType, entityId);
      return result;
    }
  );
};

export const useDeleteAttachmentMutation = () => {
  return useMutation((id: string) => erpClient.attachments.delete(id));
};

/**
 * Settings hooks - cache for 10 minutes, very stable data
 */
export const useSettingsList = () => {
  return useQuery(async () => {
    // Try IndexedDB cache first
    try {
      const cached = await settingsCache.getSettings();
      if (cached) {
        console.log('[useSettingsList] Serving from IndexedDB cache');
        return {
          success: true,
          data: cached,
          message: 'Retrieved from cache',
          timestamp: new Date().toISOString(),
        } as ApiResponse<any>;
      }
    } catch (e) {
      console.warn('[useSettingsList] Failed to read from cache', e);
    }

    if (!(erpClient as any).settings) {
      throw new Error('Settings service is not available. Please ensure the erp-service package is built.');
    }
    
    const response = await (erpClient as any).settings.list();
    
    // Update cache
    if (response.data && Array.isArray(response.data)) {
      settingsCache.setSettings(response.data).catch(e => 
        console.warn('[useSettingsList] Failed to update cache', e)
      );
    }
    
    return response;
  }, {
    queryKey: 'settings-list',
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

export const useSettingQuery = (key: string, options?: UseQueryOptions<unknown>) => {
  return useQuery(() => {
    if (!(erpClient as any).settings) {
      throw new Error('Settings service is not available. Please ensure the erp-service package is built.');
    }
    return (erpClient as any).settings.get(key);
  }, {
    enabled: !!key && options?.enabled !== false,
    ...options,
    queryKey: `setting-${key}`,
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

export const useHeaderFieldsQuery = () => {
  return useQuery(() => {
    if (!(erpClient as any).settings) {
      throw new Error('Settings service is not available. Please ensure the erp-service package is built.');
    }
    return (erpClient as any).settings.getHeaderFields();
  }, {
    queryKey: 'settings-header-fields',
    staleTime: 600000, // 10 minutes
    cacheTime: 1800000, // 30 minutes
  });
};

/**
 * Settings mutations
 */
export const useUpsertSettingMutation = () => {
  return useMutation(
    async ({ key, value }: { key: string; value: string }) => {
      if (!(erpClient as any).settings) {
        throw new Error('Settings service is not available. Please ensure the erp-service package is built.');
      }
      console.log(`[useUpsertSettingMutation] Calling PUT /api/v1/settings/${key} with value:`, value);
      const result = await (erpClient as any).settings.upsert(key, { value });
      
      // Invalidate cache on update
      settingsCache.invalidateCache().catch(e => 
        console.warn('[useUpsertSettingMutation] Failed to invalidate cache', e)
      );
      
      return result;
    }
  );
};

export const useDeleteSettingMutation = () => {
  return useMutation((key: string) => {
    if (!(erpClient as any).settings) {
      throw new Error('Settings service is not available. Please ensure the erp-service package is built.');
    }
    return (erpClient as any).settings.delete(key);
  });
};

/**
 * Invoice hooks - cache for 2 minutes, may change when settings updated
 */
export const useInvoiceRequirementsQuery = (options?: UseQueryOptions<unknown>) => {
  return useQuery(() => (erpClient as any).sales.checkInvoiceRequirements(), {
    enabled: options?.enabled !== false,
    ...options,
    queryKey: 'invoice-requirements',
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Sales Cancellation hooks - for order cancellation with inventory return
 */

/**
 * Cancel an entire sale with inventory return
 * Automatically handles inventory return, discount reversal, tax voiding
 * Only sales with status: pending, confirmed, or processing can be cancelled
 * @returns Mutation hook with mutateAsync method
 */
export const useCancelSaleMutation = () => {
  return useMutation((payload: { id: string; reason?: string; cancelled_by?: string }) =>
    erpClient.sales.cancel(payload.id, {
      reason: payload.reason,
      cancelled_by: payload.cancelled_by,
    })
  );
};

/**
 * Cancel specific items from a sale (partial cancellation)
 * Allows selective item removal with quantity control
 * @returns Mutation hook with mutateAsync method
 */
export const useCancelSaleItemsMutation = () => {
  return useMutation<
    SaleCancellationResponse,
    {
      id: string;
      items: Array<{ sale_item_id: string; quantity?: number; reason?: string }>;
      reason: string;
      reason_details?: string;
      performed_by: string;
    }
  >((payload) => {
    // Pass backend format (lowercase/snake_case) directly to service
    return erpClient.sales.cancelItems(payload.id, {
      reason: payload.reason,
      reason_details: payload.reason_details,
      performed_by: payload.performed_by,
      items: payload.items,
    } as any);
  });
};

/**
 * Get cancellation history for a sale
 * @param saleId - Sale ID
 * @param params - Optional pagination parameters
 * @returns Query hook with cancellation history
 */
export const useSaleCancellationsQuery = (saleId: string, params?: { limit?: number; offset?: number }) => {
  return useQuery(() => erpClient.sales.getCancellations(saleId, params), {
    enabled: !!saleId,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

// ============================================================================
// Aggregation API Hooks
// ============================================================================

/**
 * Get detailed product information with optional related data
 * Reduces API calls by aggregating variants, prices, inventory, etc.
 */
export const useProductDetailQuery = (
  id: string,
  params?: {
    include?: string;
    warehouse_id?: string;
    price_type?: 'retail' | 'wholesale' | 'bulk' | 'all';
    active_only?: boolean;
    in_stock_only?: boolean;
  }
) => {
  return useQuery(() => (erpClient as any).aggregation.getProductDetail(id, params), {
    enabled: !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Get detailed variant information with optional related data
 */
export const useVariantDetailQuery = (
  id: string,
  params?: {
    include?: string;
    warehouse_id?: string;
  }
) => {
  return useQuery(() => (erpClient as any).aggregation.getVariantDetail(id, params), {
    enabled: !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Get sales context data for POS/sales workflows
 * Returns products, variants, inventory, and warehouses needed for sales operations
 */
export const useSalesContextQuery = (params?: {
  warehouse_id?: string;
  include_zero_stock?: boolean;
  price_type?: string;
  effective_date?: string;
}) => {
  const queryKey = `sales-context-${JSON.stringify(params || {})}`;
  return useQuery(() => (erpClient as any).aggregation.getSalesContext(params || {}), {
    queryKey,
    staleTime: 30000, // 30 seconds - sales context changes frequently
    cacheTime: 120000, // 2 minutes
  });
};

/**
 * Get detailed purchase order information with optional related data
 */
export const usePurchaseOrderDetailQuery = (
  id: string,
  params?: {
    include?: string;
  }
) => {
  return useQuery(() => (erpClient as any).aggregation.getPurchaseOrderDetail(id, params), {
    enabled: !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * List inventory batches with filters and optional related data
 */
export const useBatchesListQuery = (params?: {
  warehouse_id?: string;
  variant_id?: string;
  product_id?: string;
  category?: string;
  in_stock_only?: boolean;
  expiring_soon?: boolean;
  low_stock_threshold?: number;
  include?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) => {
  const queryKey = `batches-list-${JSON.stringify(params || {})}`;
  return useQuery(() => (erpClient as any).aggregation.listBatches(params || {}), {
    queryKey,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

// ============================================================================
// GRN Rejection Tracking Hooks
// ============================================================================

/**
 * Get rejected items from a GRN
 * Returns all GRN items that were rejected during quality check
 */
export const useGRNRejectedItemsQuery = (grnId: string) => {
  return useQuery(() => (erpClient as any).grns.getRejectedItems(grnId), {
    enabled: !!grnId,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Update return status for a rejected GRN item
 * Tracks the return process: pending → sent → received_by_vendor → closed
 */
export const useUpdateGRNItemReturnStatusMutation = () => {
  return useMutation(
    (payload: {
      itemId: string;
      data: {
        return_status?: 'pending' | 'sent' | 'received_by_vendor' | 'closed';
        return_sent_date?: string;
        return_received_date?: string;
        return_closed_date?: string;
        return_remarks?: string;
      };
    }) => (erpClient as any).grns.updateReturnStatus(payload.itemId, payload.data)
  );
};

// ============================================================================
// Products by Category Hook
// ============================================================================

/**
 * Get products by category ID with optional subcategory filter
 */
export const useProductsByCategoryQuery = (
  categoryId: string,
  params?: {
    subcategory_id?: string;
    limit?: number;
    offset?: number;
    is_active?: boolean;
  },
  options?: { enabled?: boolean }
) => {
  const queryKey = `products-category-${categoryId}-${JSON.stringify(params || {})}`;
  const enabled = options?.enabled !== undefined ? options.enabled : !!categoryId;
  return useQuery(() => {
    const getByCategory = erpClient.products.getByCategory as (categoryId: string, params?: { subcategory_id?: string; limit?: number; offset?: number; is_active?: boolean }) => Promise<ApiResponse<any>>;
    return getByCategory(categoryId, params);
  }, {
    enabled,
    queryKey,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
  });
};

/**
 * Get products filtered by total inventory quantity across all warehouses
 * @param params - Filter parameters (min, max, limit, offset)
 * @returns Query hook with products within the specified quantity range
 */
export const useProductsByQuantity = (params: {
  min: number;
  max: number;
  limit?: number;
  offset?: number;
}) => {
  const queryKey = `products-by-quantity-${JSON.stringify(params)}`;
  // Only enable if params are valid (min >= 0, max >= min, and not the disabled sentinel values)
  const isEnabled = params.min !== undefined && params.max !== undefined && 
                    params.min >= 0 && params.max >= params.min && 
                    !(params.min === -1 && params.max === -1);
  
  console.log('[useProductsByQuantity] Hook called with:', {
    params,
    isEnabled,
    queryKey
  });
  
  // Check if service method exists (outside query function to avoid repeated checks)
  const getByQuantity = (erpClient.products as any)?.getByQuantity;
  if (!getByQuantity) {
    console.error('[useProductsByQuantity] getByQuantity method not found on erpClient.products');
    console.error('[useProductsByQuantity] Available methods:', Object.keys(erpClient.products || {}));
  } else {
    console.log('[useProductsByQuantity] Service method found, will call API when enabled:', isEnabled);
  }
  
  return useQuery(() => {
    if (!getByQuantity) {
      throw new Error('getByQuantity method not available on erpClient.products. Make sure the service is updated.');
    }
    console.log('[useProductsByQuantity] 🔥 CALLING API /api/v1/products/by-quantity with params:', params);
    return getByQuantity(params);
  }, {
    queryKey,
    staleTime: 120000, // 2 minutes
    cacheTime: 300000, // 5 minutes
    enabled: isEnabled,
  });
};

// ============================================================================
// Report Hooks
// ============================================================================

import type {
  ProductReportFilter,
  ProductReportResponse,
  VendorReportFilter,
  VendorReportResponse,
  CustomerReportFilter,
  CustomerReportResponse,
  InventoryReportFilter,
  InventoryReportResponse,
  PurchaseReportFilter,
  PurchaseReportResponse,
  SalesReportFilter,
  SalesReportResponse,
  ReturnsReportFilter,
  ReturnsReportResponse,
} from '@kisanlink/erp-service';

/**
 * Products report hook - cache for 5 minutes
 * Returns product master report with variant counts and stock levels
 */
export const useProductsReport = (filters?: ProductReportFilter) => {
  const queryKey = `reports-products-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.products(filters) as Promise<ApiResponse<ProductReportResponse>>,
    {
      queryKey,
      staleTime: 300000, // 5 minutes
      cacheTime: 600000, // 10 minutes
    }
  );
};

/**
 * Vendors report hook - cache for 5 minutes
 * Returns vendor master report with contact details and PO values
 */
export const useVendorsReport = (filters?: VendorReportFilter) => {
  const queryKey = `reports-vendors-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.vendors(filters) as Promise<ApiResponse<VendorReportResponse>>,
    {
      queryKey,
      staleTime: 300000, // 5 minutes
      cacheTime: 600000, // 10 minutes
    }
  );
};

/**
 * Customers report hook - cache for 5 minutes
 * Returns customer purchase history with revenue metrics
 */
export const useCustomersReport = (filters?: CustomerReportFilter) => {
  const queryKey = `reports-customers-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.customers(filters) as Promise<ApiResponse<CustomerReportResponse>>,
    {
      queryKey,
      staleTime: 300000, // 5 minutes
      cacheTime: 600000, // 10 minutes
    }
  );
};

/**
 * Inventory report hook - cache for 2 minutes (changes frequently)
 * Returns inventory batches with stock levels, valuations, and expiry tracking
 */
export const useInventoryReport = (filters?: InventoryReportFilter) => {
  const queryKey = `reports-inventory-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.inventory(filters) as Promise<ApiResponse<InventoryReportResponse>>,
    {
      queryKey,
      staleTime: 120000, // 2 minutes
      cacheTime: 300000, // 5 minutes
    }
  );
};

/**
 * Purchases report hook - cache for 2 minutes
 * Returns purchase order history with payment status and vendor breakdown
 */
export const usePurchasesReport = (filters?: PurchaseReportFilter) => {
  const queryKey = `reports-purchases-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.purchases(filters) as Promise<ApiResponse<PurchaseReportResponse>>,
    {
      queryKey,
      staleTime: 120000, // 2 minutes
      cacheTime: 300000, // 5 minutes
    }
  );
};

/**
 * Sales report hook - cache for 2 minutes
 * Returns sales transactions with revenue, tax, margin analysis
 */
export const useSalesReport = (filters?: SalesReportFilter) => {
  const queryKey = `reports-sales-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.sales(filters) as Promise<ApiResponse<SalesReportResponse>>,
    {
      queryKey,
      staleTime: 120000, // 2 minutes
      cacheTime: 300000, // 5 minutes
    }
  );
};

/**
 * Returns report hook - cache for 2 minutes
 * Returns return transactions with refund amounts and status breakdown
 */
export const useReturnsReport = (filters?: ReturnsReportFilter) => {
  const queryKey = `reports-returns-${JSON.stringify(filters || {})}`;
  return useQuery(
    () => erpClient.reports.returns(filters) as Promise<ApiResponse<ReturnsReportResponse>>,
    {
      queryKey,
      staleTime: 120000, // 2 minutes
      cacheTime: 300000, // 5 minutes
    }
  );
};

interface ExportReportParams {
  reportType:
    | 'products'
    | 'vendors'
    | 'customers'
    | 'inventory'
    | 'purchases'
    | 'sales'
    | 'returns';
  filters?: Record<string, unknown>;
}

interface UseExportReportMutationResult {
  mutateAsync: (params: ExportReportParams) => Promise<Blob>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Export report mutation - handles file download for xlsx/pdf formats
 * Uses direct fetch to properly handle binary responses
 * @returns Mutation hook for exporting reports
 */
export const useExportReportMutation = (): UseExportReportMutationResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(
    async ({ reportType, filters }: ExportReportParams): Promise<Blob> => {
      setIsLoading(true);
      setError(null);

      try {
        const format = (filters?.format as string) || 'xlsx';
        const baseUrl = getERPBaseUrl();

        // Build query params
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              params.append(key, String(value));
            }
          });
        }
        // Ensure format is set
        params.set('format', format);

        const url = `${baseUrl.replace(/\/$/, '')}/api/v1/reports/${reportType}?${params.toString()}`;

        // Get auth token from store
        const token = store.getState().auth.accessToken;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            Accept:
              format === 'xlsx'
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`Export failed: ${response.status} ${response.statusText}`);
        }

        return await response.blob();
      } catch (err) {
        const exportError = err instanceof Error ? err : new Error('Export failed');
        setError(exportError);
        throw exportError;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    mutateAsync,
    isLoading,
    error,
  };
};
